import * as THREE from 'three';
import { SceneSystem } from './SceneSystem';
import { pauseClock } from '../core/pause';

const RAIN_COUNT = 25000;
const RAIN_AREA = 4000;
const RAIN_HEIGHT = 500;
const RAIN_STREAK_LENGTH = 10;
const RAIN_SPEED_MIN = 8.0;
const RAIN_SPEED_MAX = 14.0;
const WIND_ANGLE = 0.25;

const LEAF_COUNT = 300;
const LEAF_AREA = 3000;
const LEAF_HEIGHT = 200;

const CLOUD_AREA = 6000;
const CLOUD_HEIGHT_MIN = 300;
const CLOUD_HEIGHT_MAX = 550;
const CLOUD_DRIFT_SPEED = 0.6;

interface CloudPuff {
  mesh: THREE.Sprite;
  baseY: number;
  driftX: number;
  driftZ: number;
  bobPhase: number;
  baseOpacity: number;
}

/** A self-contained, frame-driven world effect (Phase 0.4 toolkit). */
interface WorldEffect {
  group: THREE.Group;
  /** Called every unpaused frame. `t` is seconds since the effect started. */
  update: (t: number) => void;
  /** Dispose owned geometries/materials. The group is removed by the caller. */
  dispose: () => void;
  startedAt: number;
}

const RAG_COLORS: Record<'red' | 'amber' | 'green', number> = {
  red: 0xef4444,
  amber: 0xf59e0b,
  green: 0x10b981,
};

export class EffectsSystem {
  private scene: SceneSystem;
  private rainGroup: THREE.Group | null = null;
  private rainPositions: Float32Array | null = null;
  private rainVelocities: Float32Array | null = null;
  private rainGeometry: THREE.BufferGeometry | null = null;
  private rainCount = 0;
  private leafGroup: THREE.Group | null = null;
  private leaves: Array<{ mesh: THREE.Mesh; vx: number; vy: number; vz: number; spin: number }> = [];
  private cloudGroup: THREE.Group | null = null;
  private clouds: CloudPuff[] = [];
  private cloudTexture: THREE.Texture | null = null;
  private statusRings = new Map<string, { mesh: THREE.Mesh; phase: number }>();
  private sensorPulses = new Map<string, THREE.Mesh[]>();
  // Phase 0.4 — pluggable world-effect framework. Each effect owns a group
  // and a per-frame update; the shared loop drives them, cleanup disposes
  // them. Keyed by id so scenarios can clear individual effects.
  private worldEffects = new Map<string, WorldEffect>();
  private glowTexture: THREE.Texture | null = null;
  private animationId = 0;
  private active = true;

  constructor(scene: SceneSystem) {
    this.scene = scene;
    this.createClouds();
    this.startLoop();
  }

  private generateSmokeTexture(): THREE.Texture {
    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    // Radial gradient — soft smoke puff
    const cx = size / 2;
    const cy = size / 2;
    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
    gradient.addColorStop(0, 'rgba(255,255,255,0.6)');
    gradient.addColorStop(0.3, 'rgba(255,255,255,0.3)');
    gradient.addColorStop(0.6, 'rgba(255,255,255,0.1)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    // Add some noise for organic feel
    const imageData = ctx.getImageData(0, 0, size, size);
    for (let i = 0; i < imageData.data.length; i += 4) {
      const noise = (Math.random() - 0.5) * 20;
      imageData.data[i + 3] = Math.max(0, Math.min(255, imageData.data[i + 3] + noise));
    }
    ctx.putImageData(imageData, 0, 0);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }

  private createClouds(): void {
    this.cloudGroup = new THREE.Group();
    this.cloudTexture = this.generateSmokeTexture();

    // Create cloud formations — dense clusters of smoke sprites
    const numClusters = 55;
    for (let c = 0; c < numClusters; c++) {
      const clusterX = (Math.random() - 0.5) * CLOUD_AREA;
      const clusterZ = (Math.random() - 0.5) * CLOUD_AREA;
      const clusterY = CLOUD_HEIGHT_MIN + Math.random() * (CLOUD_HEIGHT_MAX - CLOUD_HEIGHT_MIN);
      const clusterW = 350 + Math.random() * 450;
      const clusterD = 280 + Math.random() * 320;
      const puffsInCluster = 18 + Math.floor(Math.random() * 16);

      for (let i = 0; i < puffsInCluster; i++) {
        const opacity = 0.07 + Math.random() * 0.08;
        const mat = new THREE.SpriteMaterial({
          map: this.cloudTexture,
          color: 0x556677,
          transparent: true,
          opacity,
          depthWrite: false,
          fog: false,
          blending: THREE.NormalBlending,
        });
        const sprite = new THREE.Sprite(mat);
        const spriteSize = 130 + Math.random() * 190;
        sprite.scale.set(spriteSize, spriteSize * (0.45 + Math.random() * 0.3), 1);
        sprite.position.set(
          clusterX + (Math.random() - 0.5) * clusterW,
          clusterY + (Math.random() - 0.5) * 30,
          clusterZ + (Math.random() - 0.5) * clusterD
        );
        this.cloudGroup.add(sprite);
        this.clouds.push({
          mesh: sprite,
          baseY: sprite.position.y,
          driftX: CLOUD_DRIFT_SPEED * (0.6 + Math.random() * 0.8),
          driftZ: (Math.random() - 0.5) * 0.15,
          bobPhase: Math.random() * Math.PI * 2,
          baseOpacity: opacity,
        });
      }
    }

    this.scene.add(this.cloudGroup);
  }

  setStormClouds(storm: boolean): void {
    for (const cloud of this.clouds) {
      const mat = cloud.mesh.material as THREE.SpriteMaterial;
      if (storm) {
        mat.color.set(0x1a2530);
        mat.opacity = cloud.baseOpacity * 4;
      } else {
        mat.color.set(0x556677);
        mat.opacity = cloud.baseOpacity;
      }
    }
  }

  startRain(intensity = 0.5): void {
    if (this.rainGroup) return;

    this.rainCount = Math.floor(RAIN_COUNT * (0.5 + intensity * 0.5));
    const vertCount = this.rainCount * 2;
    this.rainPositions = new Float32Array(vertCount * 3);
    this.rainVelocities = new Float32Array(this.rainCount);

    for (let i = 0; i < this.rainCount; i++) {
      const x = (Math.random() - 0.5) * RAIN_AREA;
      const y = Math.random() * RAIN_HEIGHT;
      const z = (Math.random() - 0.5) * RAIN_AREA;
      const speed = RAIN_SPEED_MIN + Math.random() * (RAIN_SPEED_MAX - RAIN_SPEED_MIN);
      this.rainVelocities[i] = speed;

      const idx = i * 6;
      this.rainPositions[idx] = x;
      this.rainPositions[idx + 1] = y;
      this.rainPositions[idx + 2] = z;
      this.rainPositions[idx + 3] = x + WIND_ANGLE * RAIN_STREAK_LENGTH;
      this.rainPositions[idx + 4] = y - RAIN_STREAK_LENGTH;
      this.rainPositions[idx + 5] = z;
    }

    this.rainGeometry = new THREE.BufferGeometry();
    this.rainGeometry.setAttribute('position', new THREE.BufferAttribute(this.rainPositions, 3));

    const material = new THREE.LineBasicMaterial({
      color: 0x8899bb,
      transparent: true,
      opacity: 0.25 + intensity * 0.2,
      linewidth: 1,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const rainLines = new THREE.LineSegments(this.rainGeometry, material);
    rainLines.frustumCulled = false;

    this.rainGroup = new THREE.Group();
    this.rainGroup.add(rainLines);
    this.scene.add(this.rainGroup);

    // Blowing leaves/debris
    this.leafGroup = new THREE.Group();
    const leafColors = [0x2d5a27, 0x4a7a3f, 0x8b6914, 0x654321, 0x3d6b35];
    for (let i = 0; i < LEAF_COUNT; i++) {
      const geo = new THREE.PlaneGeometry(1.5 + Math.random() * 1.5, 0.8 + Math.random() * 0.8);
      const mat = new THREE.MeshBasicMaterial({
        color: leafColors[Math.floor(Math.random() * leafColors.length)],
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const leaf = new THREE.Mesh(geo, mat);
      leaf.position.set(
        (Math.random() - 0.5) * LEAF_AREA,
        Math.random() * LEAF_HEIGHT + 5,
        (Math.random() - 0.5) * LEAF_AREA
      );
      leaf.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      this.leafGroup.add(leaf);
      this.leaves.push({
        mesh: leaf,
        vx: 2 + Math.random() * 4,
        vy: -0.5 - Math.random() * 1.5,
        vz: 1 + Math.random() * 2,
        spin: (Math.random() - 0.5) * 0.15,
      });
    }
    this.scene.add(this.leafGroup);
  }

  stopRain(): void {
    if (this.rainGroup) {
      this.scene.remove(this.rainGroup);
      this.rainGroup.traverse((child) => {
        if (child instanceof THREE.LineSegments) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
      this.rainGroup = null;
      this.rainGeometry = null;
      this.rainPositions = null;
      this.rainVelocities = null;
    }
    if (this.leafGroup) {
      this.scene.remove(this.leafGroup);
      for (const leaf of this.leaves) {
        leaf.mesh.geometry.dispose();
        (leaf.mesh.material as THREE.Material).dispose();
      }
      this.leafGroup = null;
      this.leaves = [];
    }
  }

  addStatusRing(id: string, coordinates: [number, number], color: string): void {
    const geo = new THREE.RingGeometry(12, 14, 32);
    const mat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(geo, mat);
    const pos = new THREE.Vector3(coordinates[0], 0, coordinates[1]);
    ring.position.copy(pos);
    ring.position.y += 1;
    ring.rotation.x = -Math.PI / 2;
    this.scene.add(ring);
    this.statusRings.set(id, { mesh: ring, phase: 0 });
  }

  removeStatusRing(id: string): void {
    const entry = this.statusRings.get(id);
    if (!entry) return;
    this.scene.remove(entry.mesh);
    entry.mesh.geometry.dispose();
    (entry.mesh.material as THREE.Material).dispose();
    this.statusRings.delete(id);
  }

  addSensorPulse(id: string, coordinates: [number, number], color: string): void {
    const pos = new THREE.Vector3(coordinates[0], 0.5, coordinates[1]);
    const col = new THREE.Color(color);
    const meshes: THREE.Mesh[] = [];

    // 3 concentric ripple rings that expand outward in sequence
    for (let i = 0; i < 3; i++) {
      const ringGeo = new THREE.RingGeometry(2, 4, 48);
      const ringMat = new THREE.MeshBasicMaterial({
        color: col,
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.copy(pos);
      ring.rotation.x = -Math.PI / 2;
      ring.userData.ripplePhase = i / 3; // staggered start
      ring.userData.rippleSpeed = 0.012;
      ring.userData.maxScale = 12;
      this.scene.add(ring);
      meshes.push(ring);
    }

    // Central glowing dot
    const dotGeo = new THREE.SphereGeometry(2.5, 12, 12);
    const dotMat = new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.9 });
    const dot = new THREE.Mesh(dotGeo, dotMat);
    dot.position.copy(pos);
    dot.position.y += 2;
    dot.userData.ripplePhase = -1; // marker: not a ripple ring
    this.scene.add(dot);
    meshes.push(dot);

    this.sensorPulses.set(id, meshes);
  }

  removeSensorPulse(id: string): void {
    const rings = this.sensorPulses.get(id);
    if (!rings) return;
    for (const ring of rings) {
      this.scene.remove(ring);
      ring.geometry.dispose();
      (ring.material as THREE.Material).dispose();
    }
    this.sensorPulses.delete(id);
  }

  private stormRipples: THREE.Mesh[] = [];

  startStormRipple(): void {
    // Subtle square border ripples expanding outward from city edge
    for (let i = 0; i < 3; i++) {
      const size = 2200;
      const thickness = 15;
      const outer = new THREE.Shape();
      outer.moveTo(-size / 2, -size / 2);
      outer.lineTo(size / 2, -size / 2);
      outer.lineTo(size / 2, size / 2);
      outer.lineTo(-size / 2, size / 2);
      outer.closePath();
      const inner = new THREE.Path();
      const inset = size / 2 - thickness;
      inner.moveTo(-inset, -inset);
      inner.lineTo(inset, -inset);
      inner.lineTo(inset, inset);
      inner.lineTo(-inset, inset);
      inner.closePath();
      outer.holes.push(inner);

      const geo = new THREE.ShapeGeometry(outer);
      const mat = new THREE.MeshBasicMaterial({
        color: 0xf59e0b,
        transparent: true,
        opacity: 0.15,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(geo, mat);
      ring.position.set(0, 2, 0);
      ring.rotation.x = -Math.PI / 2;
      ring.userData.ripplePhase = i / 3;
      ring.userData.minScale = 1.0;
      ring.userData.maxScale = 2.5;
      this.scene.add(ring);
      this.stormRipples.push(ring);
    }
  }

  stopStormRipple(): void {
    for (const ring of this.stormRipples) {
      this.scene.remove(ring);
      ring.geometry.dispose();
      (ring.material as THREE.Material).dispose();
    }
    this.stormRipples = [];
  }

  // ════════════════════════════════════════════════════════════════════
  // Phase 0.4 — World effects toolkit
  // ════════════════════════════════════════════════════════════════════

  /** Shared soft radial sprite — particles, pins, flow dots. */
  private getGlowTexture(): THREE.Texture {
    if (this.glowTexture) return this.glowTexture;
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.35, 'rgba(255,255,255,0.7)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    this.glowTexture = new THREE.CanvasTexture(canvas);
    return this.glowTexture;
  }

  private registerEffect(id: string, effect: WorldEffect): void {
    this.removeWorldEffect(id); // replace any existing effect with same id
    this.scene.add(effect.group);
    this.worldEffects.set(id, effect);
  }

  removeWorldEffect(id: string): void {
    const fx = this.worldEffects.get(id);
    if (!fx) return;
    this.scene.remove(fx.group);
    fx.dispose();
    this.worldEffects.delete(id);
  }

  /** Ruptured main — fountain of water particles + spreading flood disc. */
  startGeyser(
    id: string,
    coordinates: [number, number],
    opts: { intensity?: number; floodRadius?: number } = {},
  ): void {
    const intensity = opts.intensity ?? 1;
    const floodRadius = opts.floodRadius ?? 60;
    const [x, z] = coordinates;
    const group = new THREE.Group();

    const count = Math.floor(280 * intensity);
    const positions = new Float32Array(count * 3);
    const vel = new Float32Array(count * 3);
    const life = new Float32Array(count);
    const reseed = (i: number) => {
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * 3;
      positions[i * 3] = Math.cos(a) * r;
      positions[i * 3 + 1] = Math.random() * 4;
      positions[i * 3 + 2] = Math.sin(a) * r;
      const spread = 6 + Math.random() * 6;
      vel[i * 3] = Math.cos(a) * spread * 0.12;
      vel[i * 3 + 1] = 26 + Math.random() * 22 * intensity;
      vel[i * 3 + 2] = Math.sin(a) * spread * 0.12;
      life[i] = 0.4 + Math.random() * 0.9;
    };
    for (let i = 0; i < count; i++) reseed(i);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      size: 6,
      map: this.getGlowTexture(),
      color: 0x9ad7ff,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geo, mat);
    points.position.set(x, 0, z);
    points.frustumCulled = false;
    group.add(points);

    // Spreading flood decal on the ground.
    const floodGeo = new THREE.CircleGeometry(1, 48);
    const floodMat = new THREE.MeshBasicMaterial({
      color: 0x2b7fb8,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const flood = new THREE.Mesh(floodGeo, floodMat);
    flood.position.set(x, 0.6, z);
    flood.rotation.x = -Math.PI / 2;
    group.add(flood);

    let last = performance.now() / 1000;
    this.registerEffect(id, {
      group,
      startedAt: last,
      update: (t) => {
        const now = performance.now() / 1000;
        const dt = Math.min(0.05, now - last);
        last = now;
        for (let i = 0; i < count; i++) {
          life[i] -= dt;
          if (life[i] <= 0) { reseed(i); continue; }
          vel[i * 3 + 1] -= 60 * dt; // gravity
          positions[i * 3] += vel[i * 3] * dt;
          positions[i * 3 + 1] += vel[i * 3 + 1] * dt;
          positions[i * 3 + 2] += vel[i * 3 + 2] * dt;
          if (positions[i * 3 + 1] < 0) reseed(i);
        }
        geo.attributes.position.needsUpdate = true;
        const grow = Math.min(1, t / 6);
        flood.scale.setScalar(floodRadius * grow);
        floodMat.opacity = 0.12 + 0.25 * grow;
      },
      dispose: () => {
        geo.dispose(); mat.dispose();
        floodGeo.dispose(); floodMat.dispose();
      },
    });
  }

  /** Open excavation — a dark pit that opens + a spoil heap that grows. */
  startExcavation(
    id: string,
    coordinates: [number, number],
    opts: { radius?: number; durationMs?: number } = {},
  ): void {
    const radius = opts.radius ?? 26;
    const dur = (opts.durationMs ?? 8000) / 1000;
    const [x, z] = coordinates;
    const group = new THREE.Group();

    const pitGeo = new THREE.CircleGeometry(radius, 40);
    const pitMat = new THREE.MeshBasicMaterial({ color: 0x1a1206, transparent: true, opacity: 0.92 });
    const pit = new THREE.Mesh(pitGeo, pitMat);
    pit.position.set(x, 0.4, z);
    pit.rotation.x = -Math.PI / 2;
    pit.scale.setScalar(0.01);
    group.add(pit);

    const spoilGeo = new THREE.ConeGeometry(radius * 0.55, radius * 0.5, 12);
    const spoilMat = new THREE.MeshStandardMaterial({ color: 0x6b4f2a, roughness: 1 });
    const spoil = new THREE.Mesh(spoilGeo, spoilMat);
    spoil.position.set(x + radius * 1.3, 0, z);
    spoil.scale.setScalar(0.01);
    group.add(spoil);

    // Hazard rim — emissive amber ring around the dig.
    const rimGeo = new THREE.RingGeometry(radius, radius + 3, 40);
    const rimMat = new THREE.MeshBasicMaterial({
      color: 0xf59e0b, transparent: true, opacity: 0.8,
      side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const rim = new THREE.Mesh(rimGeo, rimMat);
    rim.position.set(x, 0.7, z);
    rim.rotation.x = -Math.PI / 2;
    group.add(rim);

    this.registerEffect(id, {
      group,
      startedAt: performance.now() / 1000,
      update: (t) => {
        const p = Math.min(1, t / dur);
        const eased = p * p * (3 - 2 * p);
        pit.scale.setScalar(Math.max(0.01, eased));
        spoil.scale.setScalar(Math.max(0.01, eased));
        rim.scale.setScalar(0.6 + 0.4 * eased);
        rimMat.opacity = 0.4 + 0.4 * Math.abs(Math.sin(t * 2));
      },
      dispose: () => {
        pitGeo.dispose(); pitMat.dispose();
        spoilGeo.dispose(); spoilMat.dispose();
        rimGeo.dispose(); rimMat.dispose();
      },
    });
  }

  /** New pipe drawing on progressively along a path over `durationMs`. */
  startPipeLay(
    id: string,
    path: [number, number][],
    opts: { color?: string; durationMs?: number } = {},
  ): void {
    if (path.length < 2) return;
    const dur = (opts.durationMs ?? 9000) / 1000;
    const curve = new THREE.CatmullRomCurve3(
      path.map(([px, pz]) => new THREE.Vector3(px, -0.5, pz)),
    );
    const tubularSegments = 240;
    const geo = new THREE.TubeGeometry(curve, tubularSegments, 2.2, 8, false);
    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(opts.color ?? '#38bdf8'),
      emissive: new THREE.Color(opts.color ?? '#0ea5e9'),
      emissiveIntensity: 0.35,
      roughness: 0.6,
      metalness: 0.4,
    });
    const tube = new THREE.Mesh(geo, mat);
    const indexPerSeg = geo.index ? geo.index.count / tubularSegments : 0;
    geo.setDrawRange(0, 0);
    const group = new THREE.Group();
    group.add(tube);

    this.registerEffect(id, {
      group,
      startedAt: performance.now() / 1000,
      update: (t) => {
        const p = Math.min(1, t / dur);
        geo.setDrawRange(0, Math.floor(indexPerSeg * tubularSegments * p));
        mat.emissiveIntensity = 0.4 + 0.4 * Math.abs(Math.sin(t * 3));
      },
      dispose: () => { geo.dispose(); mat.dispose(); },
    });
  }

  /** Glowing dots flowing along a network polyline (water/gas/sewer). */
  startFlowPulse(
    id: string,
    path: [number, number][],
    opts: { color?: string; speed?: number } = {},
  ): void {
    if (path.length < 2) return;
    const curve = new THREE.CatmullRomCurve3(
      path.map(([px, pz]) => new THREE.Vector3(px, 1.5, pz)),
    );
    const speed = opts.speed ?? 0.12;
    const dotCount = 18;
    const tex = this.getGlowTexture();
    const dots: THREE.Sprite[] = [];
    const group = new THREE.Group();
    for (let i = 0; i < dotCount; i++) {
      const mat = new THREE.SpriteMaterial({
        map: tex,
        color: new THREE.Color(opts.color ?? '#22d3ee'),
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const s = new THREE.Sprite(mat);
      s.scale.setScalar(7);
      s.userData.offset = i / dotCount;
      group.add(s);
      dots.push(s);
    }
    this.registerEffect(id, {
      group,
      startedAt: performance.now() / 1000,
      update: (t) => {
        for (const s of dots) {
          const u = (s.userData.offset + t * speed) % 1;
          curve.getPointAt(u, s.position);
        }
      },
      dispose: () => { for (const s of dots) (s.material as THREE.Material).dispose(); },
    });
  }

  /** Expanding comms ring from a hub that ignites target pins as it passes. */
  startCommsBroadcast(
    id: string,
    coordinates: [number, number],
    opts: { color?: string; radius?: number; targets?: [number, number][] } = {},
  ): void {
    const [x, z] = coordinates;
    const color = new THREE.Color(opts.color ?? '#38bdf8');
    const radius = opts.radius ?? 420;
    const group = new THREE.Group();

    const rings: THREE.Mesh[] = [];
    for (let i = 0; i < 3; i++) {
      const g = new THREE.RingGeometry(1, 1.04, 64);
      const m = new THREE.MeshBasicMaterial({
        color, transparent: true, opacity: 0.5,
        side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
      });
      const ring = new THREE.Mesh(g, m);
      ring.position.set(x, 2, z);
      ring.rotation.x = -Math.PI / 2;
      ring.userData.phase = i / 3;
      group.add(ring);
      rings.push(ring);
    }

    const tex = this.getGlowTexture();
    const pins = (opts.targets ?? []).map(([px, pz]) => {
      const m = new THREE.SpriteMaterial({
        map: tex, color, transparent: true, opacity: 0, depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const s = new THREE.Sprite(m);
      s.scale.setScalar(14);
      s.position.set(px, 8, pz);
      s.userData.dist = Math.hypot(px - x, pz - z);
      group.add(s);
      return s;
    });

    this.registerEffect(id, {
      group,
      startedAt: performance.now() / 1000,
      update: (t) => {
        for (const ring of rings) {
          const p = (ring.userData.phase + t * 0.35) % 1;
          const r = p * radius;
          ring.scale.set(r, r, r);
          (ring.material as THREE.MeshBasicMaterial).opacity = 0.5 * (1 - p);
        }
        const wave = ((t * 0.35) % 1) * radius;
        for (const pin of pins) {
          const lit = wave >= pin.userData.dist ? 1 : 0;
          const mat = pin.material as THREE.SpriteMaterial;
          mat.opacity += (lit * (0.7 + 0.3 * Math.sin(t * 6)) - mat.opacity) * 0.1;
        }
      },
      dispose: () => {
        for (const r of rings) { r.geometry.dispose(); (r.material as THREE.Material).dispose(); }
        for (const p of pins) (p.material as THREE.Material).dispose();
      },
    });
  }

  /** Many sites lit R/A/G simultaneously, optionally linked to a hub. */
  startMultiSiteRag(
    id: string,
    sites: Array<{ coordinates: [number, number]; status: 'red' | 'amber' | 'green' }>,
    linkTo?: [number, number],
  ): void {
    const group = new THREE.Group();
    const pulsing: Array<{ mat: THREE.Material & { opacity: number }; base: number }> = [];

    for (const site of sites) {
      const [sx, sz] = site.coordinates;
      const col = RAG_COLORS[site.status];

      const beamGeo = new THREE.CylinderGeometry(2.5, 2.5, 90, 12, 1, true);
      const beamMat = new THREE.MeshBasicMaterial({
        color: col, transparent: true, opacity: 0.28,
        depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      });
      const beam = new THREE.Mesh(beamGeo, beamMat);
      beam.position.set(sx, 45, sz);
      group.add(beam);
      pulsing.push({ mat: beamMat, base: 0.28 });

      const ringGeo = new THREE.RingGeometry(8, 11, 40);
      const ringMat = new THREE.MeshBasicMaterial({
        color: col, transparent: true, opacity: 0.7,
        side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(sx, 1, sz);
      ring.rotation.x = -Math.PI / 2;
      group.add(ring);
      pulsing.push({ mat: ringMat, base: 0.7 });

      if (linkTo) {
        const lineMat = new THREE.LineBasicMaterial({
          color: col, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending,
        });
        const lineGeo = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(sx, 3, sz),
          new THREE.Vector3(linkTo[0], 3, linkTo[1]),
        ]);
        group.add(new THREE.Line(lineGeo, lineMat));
      }
    }

    this.registerEffect(id, {
      group,
      startedAt: performance.now() / 1000,
      update: (t) => {
        const k = 0.7 + 0.3 * Math.sin(t * 2.5);
        for (const p of pulsing) p.mat.opacity = p.base * k;
      },
      dispose: () => {
        group.traverse((o) => {
          const m = o as THREE.Mesh | THREE.Line;
          if ('geometry' in m && m.geometry) (m.geometry as THREE.BufferGeometry).dispose();
          if ('material' in m && m.material) (m.material as THREE.Material).dispose();
        });
      },
    });
  }

  // ════════════════════════════════════════════════════════════════════
  // Combined-solution toolkit — new visuals for the "One Operating Layer"
  // story. Built on the same WorldEffect framework as the Phase 0.4 effects.
  // ════════════════════════════════════════════════════════════════════

  /**
   * CONVERGENCE — N source nodes stream glowing tokens that fuse into a single
   * target hub. The literal "three jobs → one dig" / "demand into ORP" picture.
   */
  startConvergence(
    id: string,
    sources: [number, number][],
    target: [number, number],
    opts: { color?: string; speed?: number } = {},
  ): void {
    if (!sources.length) return;
    const color = new THREE.Color(opts.color ?? '#2dd4bf');
    const speed = opts.speed ?? 0.45;
    const [tx, tz] = target;
    const tex = this.getGlowTexture();
    const group = new THREE.Group();

    // Source markers
    for (const [sx, sz] of sources) {
      const m = new THREE.SpriteMaterial({
        map: tex, color, transparent: true, opacity: 0.7,
        depthWrite: false, blending: THREE.AdditiveBlending,
      });
      const s = new THREE.Sprite(m);
      s.scale.setScalar(11);
      s.position.set(sx, 7, sz);
      group.add(s);
    }

    // Travelling tokens — a few per source, streaming toward the target
    const perSource = 4;
    const tokens: Array<{ sprite: THREE.Sprite; sx: number; sz: number; offset: number }> = [];
    for (const [sx, sz] of sources) {
      for (let k = 0; k < perSource; k++) {
        const m = new THREE.SpriteMaterial({
          map: tex, color, transparent: true, opacity: 0,
          depthWrite: false, blending: THREE.AdditiveBlending,
        });
        const sprite = new THREE.Sprite(m);
        sprite.scale.setScalar(8);
        group.add(sprite);
        tokens.push({ sprite, sx, sz, offset: k / perSource });
      }
    }

    // Fused hub at the target — a bright node + a pulsing ring
    const hubMat = new THREE.SpriteMaterial({
      map: tex, color, transparent: true, opacity: 0.9,
      depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const hub = new THREE.Sprite(hubMat);
    hub.scale.setScalar(16);
    hub.position.set(tx, 8, tz);
    group.add(hub);

    const ringGeo = new THREE.RingGeometry(7, 9, 48);
    const ringMat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0.6,
      side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.position.set(tx, 1.5, tz);
    ring.rotation.x = -Math.PI / 2;
    group.add(ring);

    this.registerEffect(id, {
      group,
      startedAt: performance.now() / 1000,
      update: (t) => {
        for (const tok of tokens) {
          const u = (tok.offset + t * speed) % 1;
          const e = u * u * (3 - 2 * u); // ease toward the hub
          tok.sprite.position.set(
            tok.sx + (tx - tok.sx) * e,
            7 + (8 - 7) * e,
            tok.sz + (tz - tok.sz) * e,
          );
          // fade in from the source, snuff out as it reaches the hub
          (tok.sprite.material as THREE.SpriteMaterial).opacity = Math.sin(u * Math.PI) * 0.95;
        }
        // hub "absorbs" — pulses on the cadence tokens arrive
        const pulse = 0.6 + 0.4 * Math.abs(Math.sin(t * speed * Math.PI * 2));
        hubMat.opacity = pulse;
        hub.scale.setScalar(14 + pulse * 5);
        const rp = (t * 0.6) % 1;
        ring.scale.setScalar(1 + rp * 2.2);
        ringMat.opacity = 0.6 * (1 - rp);
      },
      dispose: () => {
        for (const tok of tokens) (tok.sprite.material as THREE.Material).dispose();
        hubMat.dispose(); ringGeo.dispose(); ringMat.dispose();
        group.traverse((o) => {
          if (o instanceof THREE.Sprite) (o.material as THREE.Material).dispose();
        });
      },
    });
  }

  /**
   * MATCH-BEAM — ORP "ride-hailing for crews". A radar sweep rotates out from
   * the hub, flashing candidate crews as it passes, then locks onto the winner
   * (solid bright connector + ring) while the rest dim. `lockAt` ∈ 0..1 is the
   * fraction of `durationMs` after which the lock happens.
   */
  startMatchBeam(
    id: string,
    hub: [number, number],
    candidates: Array<{ coordinates: [number, number]; best?: boolean }>,
    opts: { color?: string; winColor?: string; radius?: number; durationMs?: number; lockAt?: number } = {},
  ): void {
    const [hx, hz] = hub;
    const baseColor = new THREE.Color(opts.color ?? '#2dd4bf');
    const winColor = new THREE.Color(opts.winColor ?? '#22c55e');
    const radius = opts.radius ?? 360;
    const dur = (opts.durationMs ?? 8000) / 1000;
    const lockAt = opts.lockAt ?? 0.62;
    const group = new THREE.Group();

    // Candidate rings + faint connector lines from the hub
    const cands = candidates.map((c) => {
      const [cx, cz] = c.coordinates;
      const ringGeo = new THREE.RingGeometry(9, 12, 40);
      const ringMat = new THREE.MeshBasicMaterial({
        color: baseColor.clone(), transparent: true, opacity: 0.35,
        side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(cx, 1.5, cz);
      ring.rotation.x = -Math.PI / 2;
      group.add(ring);

      const lineMat = new THREE.LineBasicMaterial({
        color: baseColor.clone(), transparent: true, opacity: 0.18, blending: THREE.AdditiveBlending,
      });
      const lineGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(hx, 3, hz), new THREE.Vector3(cx, 3, cz),
      ]);
      const line = new THREE.Line(lineGeo, lineMat);
      group.add(line);

      return { ring, ringGeo, ringMat, line, lineGeo, lineMat, best: !!c.best, angle: Math.atan2(cz - hz, cx - hx) };
    });

    // Sweep beam — a flat triangle in the XZ plane, spun about vertical.
    const beamShape = new THREE.Shape();
    beamShape.moveTo(0, 0);
    beamShape.lineTo(radius, -radius * 0.06);
    beamShape.lineTo(radius, radius * 0.06);
    beamShape.closePath();
    const beamGeo = new THREE.ShapeGeometry(beamShape);
    // ShapeGeometry lies in XY; rotate so it lies in XZ (ground plane)
    beamGeo.rotateX(-Math.PI / 2);
    const beamMat = new THREE.MeshBasicMaterial({
      color: baseColor.clone(), transparent: true, opacity: 0.25,
      side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const beam = new THREE.Mesh(beamGeo, beamMat);
    beam.position.set(hx, 2.5, hz);
    group.add(beam);

    // Hub marker
    const hubGeo = new THREE.RingGeometry(5, 7, 32);
    const hubMat = new THREE.MeshBasicMaterial({
      color: baseColor.clone(), transparent: true, opacity: 0.7,
      side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const hubRing = new THREE.Mesh(hubGeo, hubMat);
    hubRing.position.set(hx, 1.5, hz);
    hubRing.rotation.x = -Math.PI / 2;
    group.add(hubRing);

    const angleDiff = (a: number, b: number) => {
      let d = Math.abs(a - b) % (Math.PI * 2);
      if (d > Math.PI) d = Math.PI * 2 - d;
      return d;
    };

    this.registerEffect(id, {
      group,
      startedAt: performance.now() / 1000,
      update: (t) => {
        const p = Math.min(1, t / dur);
        if (p < lockAt) {
          // Sweeping phase — rotate the beam, flash candidates as it passes.
          const theta = t * 2.4;          // beam.rotation.y
          beam.rotation.y = theta;
          const pointing = -theta;        // world direction the beam points
          for (const c of cands) {
            const near = angleDiff(((pointing % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2),
                                   ((c.angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
            const lit = near < 0.25 ? 1 : 0;
            c.ringMat.opacity += (0.35 + lit * 0.5 - c.ringMat.opacity) * 0.2;
          }
          beamMat.opacity = 0.18 + 0.12 * Math.abs(Math.sin(t * 6));
        } else {
          // Lock phase — beam points at the winner; winner glows, rest dim.
          const winner = cands.find((c) => c.best) ?? cands[0];
          if (winner) {
            beam.rotation.y = -winner.angle;
            beamMat.color.copy(winColor);
            beamMat.opacity = 0.28 + 0.1 * Math.sin(t * 4);
          }
          for (const c of cands) {
            if (c.best) {
              c.ringMat.color.copy(winColor);
              c.lineMat.color.copy(winColor);
              c.ringMat.opacity = 0.85 + 0.15 * Math.sin(t * 5);
              c.lineMat.opacity = 0.7;
              c.ring.scale.setScalar(1 + 0.12 * Math.abs(Math.sin(t * 4)));
            } else {
              c.ringMat.opacity += (0.12 - c.ringMat.opacity) * 0.15;
              c.lineMat.opacity += (0.06 - c.lineMat.opacity) * 0.15;
            }
          }
        }
        hubMat.opacity = 0.55 + 0.25 * Math.abs(Math.sin(t * 3));
      },
      dispose: () => {
        beamGeo.dispose(); beamMat.dispose();
        hubGeo.dispose(); hubMat.dispose();
        for (const c of cands) {
          c.ringGeo.dispose(); c.ringMat.dispose();
          c.lineGeo.dispose(); c.lineMat.dispose();
        }
      },
    });
  }

  /**
   * GAUGE-CLUSTER — the ORP "four dials" storyboard frame, rendered in-world: a
   * row of 270° gauge arcs near a crew, each filling from 0 to its value. Built
   * from progressively-revealed tube arcs (same technique as pipe-lay).
   */
  startGaugeCluster(
    id: string,
    coordinates: [number, number],
    gauges: Array<{ value: number; color?: string }>,
    opts: { durationMs?: number; spacing?: number; gaugeRadius?: number } = {},
  ): void {
    if (!gauges.length) return;
    const [cx, cz] = coordinates;
    const fill = (opts.durationMs ?? 2500) / 1000;
    const spacing = opts.spacing ?? 26;
    const gr = opts.gaugeRadius ?? 9;
    const group = new THREE.Group();

    const ARC = Math.PI * 1.5;        // 270° sweep
    const START = Math.PI * 0.75;     // open at the bottom
    const SEGMENTS = 64;

    const arcPoints = (frac: number) => {
      const pts: THREE.Vector3[] = [];
      const n = Math.max(2, Math.floor(SEGMENTS * frac));
      for (let i = 0; i <= n; i++) {
        const a = START + ARC * frac * (i / n);
        pts.push(new THREE.Vector3(Math.cos(a) * gr, 0, Math.sin(a) * gr));
      }
      return pts;
    };

    const fgItems = gauges.map((g, i) => {
      const col = new THREE.Color(g.color ?? '#2dd4bf');
      const ox = cx + (i - (gauges.length - 1) / 2) * spacing;
      const oz = cz;

      // Faint background track (full 270°)
      const bgGeo = new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3(arcPoints(1)), SEGMENTS, 1.1, 8, false,
      );
      const bgMat = new THREE.MeshBasicMaterial({ color: 0x334155, transparent: true, opacity: 0.5 });
      const bg = new THREE.Mesh(bgGeo, bgMat);
      bg.position.set(ox, 1.2, oz);
      group.add(bg);

      // Foreground coloured fill (revealed up to value)
      const value = Math.max(0, Math.min(1, g.value));
      const fgFull = new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3(arcPoints(Math.max(0.02, value))), SEGMENTS, 1.5, 8, false,
      );
      const fgMat = new THREE.MeshStandardMaterial({
        color: col, emissive: col, emissiveIntensity: 0.5, roughness: 0.5, metalness: 0.3,
      });
      const fg = new THREE.Mesh(fgFull, fgMat);
      fg.position.set(ox, 1.3, oz);
      const idxCount = fgFull.index ? fgFull.index.count : 0;
      fgFull.setDrawRange(0, 0);
      group.add(fg);

      return { fgMat, fgGeo: fgFull, bgGeo, bgMat, idxCount };
    });

    this.registerEffect(id, {
      group,
      startedAt: performance.now() / 1000,
      update: (t) => {
        const p = Math.min(1, t / fill);
        const e = p * p * (3 - 2 * p);
        for (const it of fgItems) {
          it.fgGeo.setDrawRange(0, Math.floor(it.idxCount * e));
          it.fgMat.emissiveIntensity = 0.4 + 0.3 * Math.abs(Math.sin(t * 3));
        }
      },
      dispose: () => {
        for (const it of fgItems) { it.fgGeo.dispose(); it.fgMat.dispose(); it.bgGeo.dispose(); it.bgMat.dispose(); }
      },
    });
  }

  removeAllEffects(): void {
    for (const id of [...this.statusRings.keys()]) this.removeStatusRing(id);
    for (const id of [...this.sensorPulses.keys()]) this.removeSensorPulse(id);
    for (const id of [...this.worldEffects.keys()]) this.removeWorldEffect(id);
    this.stopStormRipple();
  }

  dispose(): void {
    this.active = false;
    cancelAnimationFrame(this.animationId);
    this.stopRain();
    this.removeAllEffects();
    if (this.cloudGroup) {
      this.scene.remove(this.cloudGroup);
      for (const cloud of this.clouds) {
        (cloud.mesh.material as THREE.Material).dispose();
      }
      this.cloudGroup = null;
      this.clouds = [];
    }
    this.cloudTexture?.dispose();
    this.glowTexture?.dispose();
  }

  private startLoop(): void {
    const loop = () => {
      if (!this.active) return;
      this.update();
      this.animationId = requestAnimationFrame(loop);
    };
    this.animationId = requestAnimationFrame(loop);
  }

  private update(): void {
    if (pauseClock.paused) return;

    if (this.worldEffects.size) {
      const now = performance.now() / 1000;
      for (const fx of this.worldEffects.values()) {
        fx.update(now - fx.startedAt);
      }
    }

    if (this.rainPositions && this.rainVelocities && this.rainGeometry) {
      const arr = this.rainPositions;
      for (let i = 0; i < this.rainCount; i++) {
        const idx = i * 6;
        const speed = this.rainVelocities[i];
        const windDrift = speed * WIND_ANGLE;
        arr[idx + 1] -= speed;
        arr[idx] += windDrift * 0.3;
        arr[idx + 4] -= speed;
        arr[idx + 3] += windDrift * 0.3;

        if (arr[idx + 4] < 0) {
          const x = (Math.random() - 0.5) * RAIN_AREA;
          const z = (Math.random() - 0.5) * RAIN_AREA;
          const y = RAIN_HEIGHT + Math.random() * 50;
          arr[idx] = x; arr[idx + 1] = y; arr[idx + 2] = z;
          arr[idx + 3] = x + WIND_ANGLE * RAIN_STREAK_LENGTH;
          arr[idx + 4] = y - RAIN_STREAK_LENGTH; arr[idx + 5] = z;
          this.rainVelocities[i] = RAIN_SPEED_MIN + Math.random() * (RAIN_SPEED_MAX - RAIN_SPEED_MIN);
        }
      }
      (this.rainGeometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    }

    for (const [, entry] of this.statusRings) {
      entry.phase = (entry.phase + 0.02) % 1;
      entry.mesh.scale.setScalar(1 + entry.phase * 2);
      (entry.mesh.material as THREE.MeshBasicMaterial).opacity = 0.8 - entry.phase * 0.8;
    }

    for (const [, markers] of this.sensorPulses) {
      for (const marker of markers) {
        const phase = marker.userData.ripplePhase;
        if (phase === -1) {
          // Central dot — gentle pulsing
          const t = (Date.now() % 1500) / 1500;
          (marker.material as THREE.MeshBasicMaterial).opacity = 0.7 + Math.sin(t * Math.PI * 2) * 0.3;
          const s = 1 + Math.sin(t * Math.PI * 2) * 0.15;
          marker.scale.setScalar(s);
        } else {
          // Ripple ring — expand outward then reset
          const speed = marker.userData.rippleSpeed;
          const maxScale = marker.userData.maxScale;
          marker.userData.ripplePhase = (phase + speed) % 1;
          const p = marker.userData.ripplePhase;
          const scale = 1 + p * maxScale;
          marker.scale.setScalar(scale);
          (marker.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - p);
        }
      }
    }

    // Animate leaves — wind-blown tumbling
    for (const leaf of this.leaves) {
      const m = leaf.mesh;
      m.position.x += leaf.vx;
      m.position.y += leaf.vy;
      m.position.z += leaf.vz;
      m.rotation.x += leaf.spin;
      m.rotation.y += leaf.spin * 0.7;
      m.rotation.z += leaf.spin * 0.4;

      leaf.vy += (Math.random() - 0.55) * 0.1;
      leaf.vy = Math.max(-3, Math.min(1, leaf.vy));

      if (m.position.x > LEAF_AREA / 2 || m.position.y < 0 || m.position.z > LEAF_AREA / 2) {
        m.position.set(
          -LEAF_AREA / 2 + Math.random() * LEAF_AREA * 0.3,
          Math.random() * LEAF_HEIGHT + 10,
          (Math.random() - 0.5) * LEAF_AREA
        );
        leaf.vy = -0.5 - Math.random() * 1.5;
      }
    }

    // Storm ripples — square borders expanding outward
    for (const ring of this.stormRipples) {
      const phase = (ring.userData.ripplePhase + 0.004) % 1;
      ring.userData.ripplePhase = phase;
      const minS = ring.userData.minScale;
      const maxS = ring.userData.maxScale;
      const scale = minS + phase * (maxS - minS);
      ring.scale.setScalar(scale);
      (ring.material as THREE.MeshBasicMaterial).opacity = 0.15 * (1 - phase);
    }

    // Animate clouds — drift, bob, rotate (smoky swirl)
    for (const cloud of this.clouds) {
      const m = cloud.mesh;
      m.position.x += cloud.driftX;
      m.position.z += cloud.driftZ;
      cloud.bobPhase += 0.004;
      m.position.y = cloud.baseY + Math.sin(cloud.bobPhase) * 10;

      // Slow rotation gives smoky turbulence feel
      (m.material as THREE.SpriteMaterial).rotation += 0.002 * (cloud.driftX > 0.4 ? 1 : -1);

      if (m.position.x > CLOUD_AREA / 2) m.position.x = -CLOUD_AREA / 2;
      if (m.position.z > CLOUD_AREA / 2) m.position.z = -CLOUD_AREA / 2;
    }
  }
}
